import React from 'react';
import { ExtendedBrand, getBankAccountNo, getBankHolderName, getBankIfsc } from '../BrandTypes';
import { Button } from '../../../components/ui/Button';
import { Edit } from 'lucide-react';

interface BankKycTabProps {
  selectedBrand: ExtendedBrand;
  isBankKycEditing: boolean;
  bankKycData: {accountNo: string; holderName: string; ifsc: string} | null;
  errors: Record<string, string>;
  handleBankKycChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setIsBankKycEditing: React.Dispatch<React.SetStateAction<boolean>>;
  saveBankKyc: () => void;
  isUpdating: boolean;
  canManageBrands: boolean;
  handleEditBankKyc: () => void;
}

export const BankKycTab: React.FC<BankKycTabProps> = ({
  selectedBrand,
  isBankKycEditing,
  bankKycData,
  errors,
  handleBankKycChange,
  setIsBankKycEditing,
  saveBankKyc,
  isUpdating,
  canManageBrands,
  handleEditBankKyc
}) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="font-medium text-gray-700">Bank KYC Details</h3>
        {selectedBrand.kyc?.bank && !isBankKycEditing && canManageBrands && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleEditBankKyc}
            className="text-xs"
          >
            <Edit size={14} className="mr-1" /> Edit Bank KYC
          </Button>
        )}
      </div>
      
      <div className="px-6 py-4">
        {isBankKycEditing && bankKycData ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Account Number */}
              <div>
                <label htmlFor="accountNo" className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="accountNo"
                  name="accountNo"
                  type="text"
                  value={bankKycData.accountNo}
                  onChange={handleBankKycChange}
                  className={`w-full px-3 py-2 border ${errors.accountNo ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter account number"
                />
                {errors.accountNo && (
                  <p className="mt-1 text-xs text-red-500">{errors.accountNo}</p>
                )}
              </div>
              
              {/* Account Holder */}
              <div>
                <label htmlFor="holderName" className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="holderName"
                  name="holderName"
                  type="text"
                  value={bankKycData.holderName}
                  onChange={handleBankKycChange}
                  className={`w-full px-3 py-2 border ${errors.holderName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter account holder name"
                />
                {errors.holderName && (
                  <p className="mt-1 text-xs text-red-500">{errors.holderName}</p>
                )}
              </div>
              
              {/* IFSC Code */}
              <div>
                <label htmlFor="ifsc" className="block text-sm font-medium text-gray-700 mb-1">
                  IFSC Code <span className="text-red-500">*</span>
                </label>
                <input
                  id="ifsc"
                  name="ifsc"
                  type="text"
                  value={bankKycData.ifsc}
                  onChange={handleBankKycChange}
                  className={`w-full px-3 py-2 border ${errors.ifsc ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter IFSC code"
                />
                {errors.ifsc && (
                  <p className="mt-1 text-xs text-red-500">{errors.ifsc}</p>
                )}
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsBankKycEditing(false)}
              >
                Cancel
              </Button>
              <Button
                isLoading={isUpdating}
                onClick={saveBankKyc}
              >
                {isUpdating ? 'Saving...' : 'Save Bank Details'}
              </Button>
            </div>
          </div>
        ) : selectedBrand.kyc?.bank ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Account Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <div className="flex items-center">
                <p className="font-medium text-gray-800">
                  {getBankAccountNo(selectedBrand) || "•••••••••••"} 
                </p>
              </div>
            </div>
            
            {/* Account Holder */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Holder
              </label>
              <div className="flex items-center">
                <p className="font-medium text-gray-800">
                  {getBankHolderName(selectedBrand) || "Not provided"}
                </p>
              </div>
            </div>
            
            {/* IFSC Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IFSC Code
              </label>
              <div className="flex items-center">
                <p className="font-medium text-gray-800">
                  {getBankIfsc(selectedBrand) || "Not provided"}
                </p>
              </div>
            </div>
            
            {/* KYC Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                KYC Status
              </label>
              <div className="flex items-center">
                <span className="bg-green-200 text-green-900 px-2 py-1 rounded-full text-sm font-semibold border border-green-300">
                  Verified
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">Bank KYC Not Completed</h3>
            <p className="text-gray-500 mb-4">No bank details have been provided yet.</p>
            {canManageBrands && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleEditBankKyc}
              >
                Add Bank Details
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 